import React from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { CheckCircle2, MapPin, Clock, QrCode, Building } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import type { PatrolScanResult } from "../types";

function formatScannedAt(iso: string): string {
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      dateStyle: "medium",
      timeStyle: "short",
    });
  } catch {
    return iso;
  }
}

export function ScanSuccessPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const scanResult = (location.state as { scanResult?: PatrolScanResult } | null)?.scanResult;

  const handleContinuePatrol = () => {
    navigate("/patrol/scan");
  };

  const handleReportIssue = () => {
    toast({
      title: "Report issue",
      description: "Use the messages or contact your manager to report an issue.",
    });
  };

  if (!scanResult) {
    return (
      <div className="flex flex-col h-full items-center justify-center p-6 bg-gray-50">
        <p className="text-gray-500 mb-4">No scan result found.</p>
        <Button onClick={() => navigate("/patrol/scan")} variant="outline">
          Back to scanner
        </Button>
      </div>
    );
  }

  const { qr_code, scanned_at } = scanResult;

  return (
    <div className="flex flex-col h-full bg-gray-50">
      <div className="flex-1 overflow-y-auto p-4">
        <div className="flex flex-col items-center mb-6">
          <CheckCircle2 className="h-16 w-16 text-green-600 mb-3" aria-hidden />
          <h2 className="text-xl font-semibold text-gray-900">Scan Successful</h2>
          <p className="text-gray-600 text-sm">Checkpoint recorded successfully</p>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-4 space-y-3 mb-6">
          <div className="flex items-center gap-3">
            <MapPin className="h-5 w-5 text-gray-500 flex-shrink-0" />
            <div>
              <p className="font-medium text-gray-900">{qr_code.name}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Clock className="h-5 w-5 text-gray-500 flex-shrink-0" />
            <p className="text-gray-700 text-sm">{formatScannedAt(scanned_at)}</p>
          </div>
          <div className="flex items-center gap-3">
            <QrCode className="h-5 w-5 text-gray-500 flex-shrink-0" />
            <p className="text-gray-700 text-sm font-mono">{qr_code.payload}</p>
          </div>
          {qr_code.description && (
            <div className="flex items-center gap-3">
              <Building className="h-5 w-5 text-gray-500 flex-shrink-0" />
              <p className="text-gray-700 text-sm">{qr_code.description}</p>
            </div>
          )}
        </div>

        <div className="space-y-3">
          <Button
            onClick={handleContinuePatrol}
            className="w-full flex items-center justify-center"
          >
            Continue Patrol
          </Button>
          <Button
            onClick={handleReportIssue}
            variant="outline"
            className="w-full flex items-center justify-center"
          >
            Report Issue
          </Button>
        </div>
      </div>
    </div>
  );
}
